const DEFAULT_TEMPLATE = '{{form_name}}_{{client_name}}_{{upload_timestamp}}'

type FormFileNamingKey =
    | 'appointment_date'
    | 'appointment_time'
    | 'client_completed_at_date'
    | 'client_completed_at_time'
    | 'client_name'
    | 'client_first_name'
    | 'client_last_name'
    | 'patient_name'
    | 'form_name'
    | 'upload_timestamp'

export type FormFileNamingContext = {
    formName?: string | null
    clientFirstName?: string | null
    clientLastName?: string | null
    patientName?: string | null
    appointmentStartTime?: number | Date | null
    clientCompletedAt?: number | Date | null
    timeZone?: string | null
}

export function resolveFormFileName(
    template: string | null | undefined,
    context: FormFileNamingContext,
): string {
    const uploadTimestamp = Date.now()
    const resolvedTemplate = template || DEFAULT_TEMPLATE

    const clientName = [context.clientFirstName, context.clientLastName]
        .filter(value => Boolean(value?.trim()))
        .join(' ')
        .trim()

    const startTime = context.appointmentStartTime
        ? new Date(context.appointmentStartTime)
        : undefined

    const clientCompletedAt = context.clientCompletedAt
        ? new Date(context.clientCompletedAt)
        : undefined

    const mapper: Record<FormFileNamingKey, string> = {
        form_name: context.formName?.trim() || '',
        client_name: clientName,
        client_first_name: context.clientFirstName?.trim() || '',
        client_last_name: context.clientLastName?.trim() || '',
        patient_name: context.patientName?.trim() || '',
        appointment_date: formatDate(startTime, context.timeZone),
        appointment_time: formatTime(startTime, context.timeZone),
        client_completed_at_date: formatDate(
            clientCompletedAt,
            context.timeZone,
        ),
        client_completed_at_time: formatTime(
            clientCompletedAt,
            context.timeZone,
        ),
        upload_timestamp: String(uploadTimestamp),
    }

    const result = (Object.keys(mapper) as FormFileNamingKey[]).reduce(
        (prev, key) => {
            const keyExp = new RegExp(`{{\\s*${key}\\s*}}`, 'g')
            if (keyExp.test(prev)) {
                return prev.replace(keyExp, mapper[key] || '')
            }
            return prev
        },
        resolvedTemplate,
    )

    const sanitized = result.replace(/\.pdf$/i, '')
    return `${sanitized}.pdf`
}

function formatDate(date: Date | undefined, timeZone?: string | null): string {
    if (!date) return ''
    try {
        return new Intl.DateTimeFormat('en-US', {
            timeZone: timeZone || undefined,
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        }).format(date)
    } catch {
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        }).format(date)
    }
}

function formatTime(date: Date | undefined, timeZone?: string | null): string {
    if (!date) return ''
    try {
        return new Intl.DateTimeFormat('en-US', {
            timeZone: timeZone || undefined,
            hour: 'numeric',
            minute: '2-digit',
        }).format(date)
    } catch {
        return new Intl.DateTimeFormat('en-US', {
            hour: 'numeric',
            minute: '2-digit',
        }).format(date)
    }
}
